import { ComponentFactoryResolver, Directive, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BiddingHelpComponent } from '../help/bidding/bidding-help.component';
import { CalendarHelpComponent } from '../help/calendar/calendar-help.component';
import { CanvasHelpComponent } from '../help/canvas/canvas-help.component';
import { DashboardHelpComponent } from '../help/dashboard/dashboard-help.component';
import { DropAddHelpComponent } from '../help/drop-add/drop-add-help.component';
import { EventsHelpComponent } from '../help/events/events-help.component';
import { JobPostingsHelpComponent } from '../help/job-postings/job-postings-help.component';
import { MyCoursesHelpComponent } from '../help/my-courses/my-courses-help.component';
import { NotFoundHelpComponent } from '../help/not-found/not-found-help.component';
import { LogCategory, LogCoreComponent, LoggerService, LogType } from '../services/logger.service';
import { SidebarService } from '../services/sidebar.service';
import { CurrentBidHelpComponent } from '../help/bidding/currentbid-help.component';
import { LostBidHelpComponent } from '../help/bidding/lostbid-help.component';
import { WonBidHelpComponent } from '../help/bidding/wonbid-help.component';
import { BidCalendarHelpComponent } from '../help/bidding/bidcalendar-help.component';
import { BidTimelineHelpComponent } from '../help/bidding/bidtimeline-help.component';
import { BidPointsHelpComponent } from '../help/bidding/bidpoints-help.component';
import { TransferCreditsHelpComponent } from '../help/transfer-credits/transfer-credits-help.component';
import { CatalogHelpComponent } from '../help/catalog/catalog-help.component';
import { MyDegreeHelpComponent } from '../help/my-degree/my-degree-help.component';
import { FacultyCoursesHelpComponent } from '../help/faculty-courses/faculty-courses-help.component';

@Directive({
    selector: '[appSidebarContent]',
    standalone: false
})
export class SidebarContentDirective implements OnInit, OnDestroy {

    private unsubscribe$: Subject<void> = new Subject();

    constructor(
        public viewContainerRef: ViewContainerRef,        
        private logger: LoggerService,
        private sidebarSvc: SidebarService
    ) {
        this.log("constructor", "", LogCategory.Constructor);
    }

    ngOnInit() {
        this.log("ngOnInit");
        this.getContent();
    }

    private getContent(): any {
        this.sidebarSvc.content$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(content => this.loadComponent(content));
    }

    private loadComponent(componentName) {        
        let component = this.getContentComponent(componentName.name);       
        this.viewContainerRef.clear();
        this.viewContainerRef.createComponent(component);
    }
        
    private getContentComponent(name): any {
        // This method matches Widgets with their Help Component by Widget Name since Widget Title can change in the label setting.
        // Other Help Components that are not associated with Widgets
        // (Current Bid, Lost Bid, Won Bid, Bid Calendar, Bid Timeline, Bid Points, Catalog)
        // are associated by Title passed into the Sidebar Service onHelp method's name paramenter.
        switch (name) {            
            case "dashboard":
                return DashboardHelpComponent;
            case "MyCourses":
                return MyCoursesHelpComponent;
            case "Calendar":
                return CalendarHelpComponent;
            case "CanvasAssignments":
                return CanvasHelpComponent;
            case "JobPostings":
                return JobPostingsHelpComponent;
            case "Events":
                return EventsHelpComponent;
            case "DropAdd":
                return DropAddHelpComponent;
            case "Bidding":
                return BiddingHelpComponent;
            case "Current Bid":
                return CurrentBidHelpComponent;
            case "Lost Bid":
                return LostBidHelpComponent;
            case "Won Bid":
                return WonBidHelpComponent;
            case "Bid Calendar":
                return BidCalendarHelpComponent;
            case "Bid Timeline":
                return BidTimelineHelpComponent;
            case "Bid Points":
                return BidPointsHelpComponent;
            case "TransferCredits":
                return TransferCreditsHelpComponent;
            case "Catalog":
                return CatalogHelpComponent;
            case "MyDegree":
                return MyDegreeHelpComponent;
            case "FacultyCourses":
                return FacultyCoursesHelpComponent;
            default:
                return NotFoundHelpComponent;
        }
    }

    private log(method: string = null, message = null, category: LogCategory = LogCategory.Default): void {
        method = (method) ? '.' + method + '()' : '';
        message = (message) ? message.toString() : '';
        this.logger.try(this.constructor.name + method + ': ' + message, LogType.CoreComponent, LogCoreComponent.Sidebar, category);
    }

    ngOnDestroy() {
        this.log("ngOnDestroy");
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
